import { useState, useMemo, useCallback } from 'react';
import { Controller, useFormContext, useFieldArray, useFormState } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import FormHelperText from '@mui/material/FormHelperText';
import { Input } from '@components/Input';
import Tooltip from '@components/Tooltip';
import { Switch } from '@components/Switch';
import Autocomplete from '@components/Autocomplete';
import FieldWithActions, { rowActions } from '@components/FieldWithActions';
import { useTranslation } from '@hooks';
import { FORM_TYPE } from '@constants/brands';

const LocationWrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    '@media (max-width: 600px)': {
        gap: '24px',
    },
});

const Settings = ({ locations, modal, selectedBrand, defaultBrandId }) => {
    const [searchLocation, setSearchLocation] = useState({});

    const { control, setValue, watch, getValues } = useFormContext();
    const { t } = useTranslation();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'locations',
    });
    const { errors, isSubmitting } = useFormState({ control });
    const locationsError = errors.locations?.root ?? errors.locations;

    const watchLocations = watch('locations');
    const selectedLocations = fields.map((_, index) => {
        return { ...watchLocations[index], id: watchLocations[index]['id'][0]?.id };
    });

    const availableLocations = useMemo(() => {
        const filteredLocations = locations.filter(l =>
            modal.type === FORM_TYPE.NEW ? !l.brand : !l.brand || l.brand?.id === selectedBrand?.id,
        );
        return filteredLocations.filter(l => !selectedLocations.some(sl => +sl.id === l.companyId));
    }, [locations, modal.type, selectedLocations]);

    const optionsWithSearch = useCallback(
        fieldIndex => {
            const filteredLocations = searchLocation[fieldIndex]
                ? availableLocations.filter(l =>
                      l.internalName.toLowerCase().includes(searchLocation[fieldIndex]?.toLowerCase()),
                  )
                : availableLocations;
            return filteredLocations.map(l => ({ id: `${l.companyId}`, label: l.internalName }));
        },
        [availableLocations, searchLocation],
    );
    const isDefaultBrandAssigned = !!defaultBrandId && defaultBrandId !== selectedBrand?.id;

    return (
        <>
            <Controller
                control={control}
                name="title"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('common.title')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <Controller
                control={control}
                name="isDefault"
                render={({ field, ref }) => (
                    <Tooltip
                        placement="top"
                        title={isDefaultBrandAssigned ? t('SettingsBrands.brandIsAlreadyAssigned') : null}
                    >
                        <span>
                            <Switch
                                {...field}
                                ref={ref}
                                checked={getValues('isDefault')}
                                handleChange={event => {
                                    setValue('isDefault', event.target.checked, { shouldDirty: true });
                                }}
                                label={t('SettingsBrands.automaticallyAssignBrand')}
                                disabled={isDefaultBrandAssigned}
                            />
                        </span>
                    </Tooltip>
                )}
            />
            <LocationWrapper>
                {fields.map((formField, index) => (
                    <FieldWithActions
                        key={formField.id}
                        actions={rowActions({
                            index,
                            count: fields.length,
                            onAdd: () => append({ id: '' }),
                            onDelete: () => remove(index),
                            canAdd: availableLocations.length > 0,
                            disabled: isSubmitting,
                            t,
                        })}
                    >
                        <Controller
                            control={control}
                            name={`locations[${index}].id`}
                            render={({ field: { ref, ...field } }) => (
                                <Autocomplete
                                    {...field}
                                    inputRef={ref}
                                    label={t('common.location')}
                                    options={optionsWithSearch(formField.id)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    disabled={isSubmitting}
                                    required={index === 0}
                                    inputProps={{
                                        value: searchLocation[formField.id] ?? '',
                                        onChange: e =>
                                            setSearchLocation(prev => ({
                                                ...prev,
                                                [formField.id]: e.target.value,
                                            })),
                                        style: { display: field?.value?.length > 0 ? 'none' : 'block' },
                                    }}
                                    onChangeAutoComplete={items => {
                                        setValue(`locations[${index}].id`, items.length ? items : [], {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                        setSearchLocation(prev => ({ ...prev, [formField.id]: '' }));
                                    }}
                                />
                            )}
                        />
                    </FieldWithActions>
                ))}
                {locationsError?.message && <FormHelperText error>{t(locationsError.message)}</FormHelperText>}
            </LocationWrapper>
        </>
    );
};

export default Settings;
